import { useAsyncFn } from 'react-use';
import { Distributor__factory } from '@angleprotocol/sdk';
import axios from 'axios';
import { useAddress, useChainId, useSigner } from '@thirdweb-dev/react';
import { MerklUserRewards } from '@app/hooks/merkl/useMerklUserRewards';
import { showErrorToast, showSuccessToast } from '@app/components/Toast';
import { ExternalLink } from '@app/components/ExternalLink';
import React from 'react';
import { QueryKeys } from '@app/constants/queryKeys';
import { useConfig } from '@app/config';
import { Mode, Optimism } from '@thirdweb-dev/chains';
import { ApplicationConfig } from '@app/config/types';
import { useQueryClient } from '@tanstack/react-query';

function getDistributorContractAddress(
  chainId: number,
  config: ApplicationConfig
) {
  switch (chainId) {
    case Optimism.chainId: {
      return '0x3Ef3D8bA38EBe18DB133cEc108f4D14CE00Dd9Ae';
    }
    case Mode.chainId:
    default: {
      return config.MERKL.DISTRIBUTOR;
    }
  }
}

export function useHarvestMerkleRewards(rewardsChainIds: number[]) {
  const chainId = useChainId();
  const signer = useSigner();
  const address = useAddress();
  const config = useConfig();
  const queryClient = useQueryClient();

  const [{ loading }, harvestRewards] = useAsyncFn(async () => {
    if (!address || !signer || !chainId || !config) {
      return;
    }

    let data: MerklUserRewards;

    try {
      data = (
        await axios.get(
          `${config?.URLS.merklApiUrl}/userRewards?chainId=${chainId}&user=${address}&proof=true`,
          {
            timeout: 5000
          }
        )
      ).data;
    } catch {
      throw 'Angle API not responding';
    }

    const tokens = Object.keys(data).filter(
      k => data[k].proof !== undefined || data[k].proof.length
    );

    const claims = tokens.map(t => data[t].accumulated);
    const proofs = tokens.map(t => data[t].proof);

    if (tokens.length === 0) throw 'No tokens to claim';

    const contractAddress = getDistributorContractAddress(chainId, config);

    if (!contractAddress) throw 'Chain not supported';

    try {
      const contract = Distributor__factory.connect(contractAddress, signer);

      const { transactionHash } = await (
        await contract.claim(
          tokens.map(() => address),
          tokens,
          claims,
          proofs as string[][]
        )
      ).wait();

      showSuccessToast(
        `Transaction successful`,
        <ExternalLink txHash={transactionHash} />
      );

      await queryClient.invalidateQueries([QueryKeys.MERKL_CAMPAIGNS]);
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.MERKL_USER_REWARDS]
      });
    } catch (e) {
      if (
        e &&
        typeof e === 'object' &&
        'reason' in e &&
        e.reason === 'user rejected transaction'
      ) {
        return;
      }

      console.error(e);

      showErrorToast(`Transaction failed`);
    }
  }, [chainId, signer, address, config, queryClient]);

  return {
    isLoading: loading,
    harvestRewards
  };
}
